import { useCallback, useState } from 'react'
import { collection, query, where, getDocs, writeBatch, doc, Timestamp, limit as firestoreLimit } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { useQueryClient } from '@tanstack/react-query'
import type { DocumentPreview, BulkDeleteResult } from '@/types'
import { getCollectionMetadata } from '@/lib/collections'
import { adminDeleteUser } from '@/lib/admin-functions'
import { pickTimestamp } from '@/lib/normalize'

export interface DocumentPreviewOptions {
  collection: string
  olderThanDays: number
  excludeActive: boolean
}

/** Most documents fetched per timestamp spelling. */
const PREVIEW_LIMIT = 1000

/**
 * Documents older than the cutoff, optionally only those explicitly marked inactive.
 *
 * Each timestamp spelling is queried on its own (a single-field range needs no
 * composite index) and the results merged. A document's age is its first present
 * timestamp in the collection's priority order, so one carrying both spellings is
 * judged once. "Exclude active" is applied here rather than as a second `where`:
 * that pairing needed a composite index that does not exist, and documents without
 * any `isActive` field count as active, which a query cannot express.
 */
export async function fetchOldDocuments(options: DocumentPreviewOptions): Promise<DocumentPreview[]> {
  const metadata = getCollectionMetadata(options.collection)
  if (!metadata) {
    throw new Error(`Collection ${options.collection} not found`)
  }

  const cutoffDate = new Date()
  cutoffDate.setDate(cutoffDate.getDate() - options.olderThanDays)
  const cutoff = Timestamp.fromDate(cutoffDate)
  const collectionRef = collection(db, options.collection)

  const snapshots = await Promise.all(
    metadata.timestampFields.map((field) =>
      getDocs(query(collectionRef, where(field, '<', cutoff), firestoreLimit(PREVIEW_LIMIT)))
    )
  )

  const byId = new Map<string, DocumentPreview>()
  for (const snapshot of snapshots) {
    for (const d of snapshot.docs) {
      if (!byId.has(d.id)) byId.set(d.id, { id: d.id, ...d.data() })
    }
  }

  return [...byId.values()].filter((d) => {
    const age = pickTimestamp(d, ...metadata.timestampFields)
    if (!age || age.toMillis() >= cutoff.toMillis()) return false
    if (options.excludeActive) {
      const active = d.isActive ?? d.is_active
      return active === false
    }
    return true
  })
}

export function useDocumentPreview() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [documents, setDocuments] = useState<DocumentPreview[]>([])

  const fetchDocuments = useCallback(async (options: DocumentPreviewOptions) => {
    setLoading(true)
    setError(null)
    setDocuments([])

    try {
      setDocuments(await fetchOldDocuments(options))
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch documents'
      setError(errorMessage)
      console.error('Error fetching documents:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    documents,
    loading,
    error,
    fetchDocuments,
  }
}

export function useBulkDelete() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<BulkDeleteResult | null>(null)
  const queryClient = useQueryClient()

  const deleteDocuments = async (
    collectionName: string,
    documentIds: string[]
  ): Promise<BulkDeleteResult> => {
    setLoading(true)
    setError(null)
    setResult(null)

    const metadata = getCollectionMetadata(collectionName)
    if (!metadata) {
      const error = `Collection ${collectionName} not found`
      setError(error)
      setLoading(false)
      throw new Error(error)
    }

    if (!metadata.canBulkDelete) {
      const error = `Bulk deletion is not allowed for collection ${collectionName}`
      setError(error)
      setLoading(false)
      throw new Error(error)
    }

    const deletionResult: BulkDeleteResult = {
      success: true,
      deletedCount: 0,
      failedCount: 0,
      errors: [],
    }

    try {
      if (metadata.hasAuth && collectionName === 'users') {
        for (const docId of documentIds) {
          try {
            await adminDeleteUser({ uid: docId })
            deletionResult.deletedCount++
          } catch (err) {
            deletionResult.failedCount++
            deletionResult.errors.push({
              id: docId,
              error: err instanceof Error ? err.message : 'Unknown error',
            })
          }
        }
      } else {
        const BATCH_SIZE = 500
        const batches = Math.ceil(documentIds.length / BATCH_SIZE)

        for (let i = 0; i < batches; i++) {
          const batchIds = documentIds.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE)
          const batch = writeBatch(db)

          for (const docId of batchIds) {
            const docRef = doc(db, collectionName, docId)
            batch.delete(docRef)
          }

          try {
            await batch.commit()
            deletionResult.deletedCount += batchIds.length
          } catch (err) {
            deletionResult.failedCount += batchIds.length
            batchIds.forEach(id => {
              deletionResult.errors.push({
                id,
                error: err instanceof Error ? err.message : 'Batch deletion failed',
              })
            })
          }
        }
      }

      if (deletionResult.failedCount > 0) {
        deletionResult.success = false
      }

      await queryClient.invalidateQueries({ queryKey: [collectionName] })

      setResult(deletionResult)
      return deletionResult
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete documents'
      setError(errorMessage)
      deletionResult.success = false
      setResult(deletionResult)
      throw err
    } finally {
      setLoading(false)
    }
  }

  return {
    deleteDocuments,
    loading,
    error,
    result,
  }
}
